<template>
    <div class="flex items-stretch gap-2 col-span-full mb-2">
        <a
            :href="`#/org-units/${props.modelValue.id}/groups`"
            class="group hidden w-[78px] relative items-center justify-center appearance-none rounded-r-sm rounded-l-2xl bg-white shadow border border-solid border-white hover:bg-sky-100 lg:flex"
        >
            <div
                class="absolute left-0 right-0 w-full h-full flex items-center justify-center text-[28px] transition text-slate-500 ease-out duration-200 group-hover:-rotate-6 group-hover:-translate-y-0.5 origin-right group-hover:ease-[cubic-bezier(0,2,.5,1)] group-hover:duration-500"
            >
                <font-awesome-icon
                    fixedWidth
                    class="absolute transition-opacity text-slate-500 group-hover:opacity-0 will-change-transform"
                    :icon="faFolder"
                ></font-awesome-icon>
                <font-awesome-icon
                    fixedWidth
                    class="absolute transition-opacity opacity-0 group-hover:opacity-100 text-sky-500"
                    :icon="faFolderOpen"
                ></font-awesome-icon>
            </div>
            <span
                class="relative transition font-semibold text-[10px] text-white translate-y-0.5 group-hover:duration-500 group-hover:ease-[cubic-bezier(0,1.5,.5,1)] group-hover:-translate-y-[18px] group-hover:text-sky-500"
                >{{ loadingGroupsCount ? '...' : formatCount(groupsCount) }}</span
            >
        </a>
        <vx-box
            class="flex-col flex w-full gap-x-6 pl-6 p-4 gap-1 lg:flex-row lg:items-center lg:gap-4 lg:rounded-l-sm"
        >
            <div
                class="font-bold text-slate-700 leading-tight line-clamp-2 overflow-hidden text-ellipsis break-all lg:w-60"
            >
                {{ props.modelValue.name }}
            </div>

            <vx-table-row-item>
                <template v-slot:label> Users: </template>
                <vx-icon-loading v-if="loadingUsersCount" spin></vx-icon-loading>
                <span v-else>{{ usersCount }}</span>
            </vx-table-row-item>

            <div class="lg:hidden">
                <vx-table-row-item>
                    <template v-slot:label> Groups: </template>
                    <vx-icon-loading v-if="loadingGroupsCount" spin></vx-icon-loading>
                    <span v-else>{{ groupsCount }}</span>
                </vx-table-row-item>
            </div>

            <div class="flex gap-1 lg:ml-auto" v-if="isAdminOrOwner">
                <vx-dropdown placement="right">
                    <vx-button data-test="dropdown" type="button" color="muted" outline class="w-[45px]">
                        <font-awesome-icon widthAuto :icon="faEllipsis" />
                    </vx-button>

                    <template v-slot:items>
                        <!-- Show enabled delete for non-billable OUs -->
                        <vx-dropdown-item
                            v-if="!props.modelValue.is_billable"
                            data-test="delete"
                            @click="deleteOrgUnit()"
                        >
                            Delete
                            <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                        </vx-dropdown-item>

                        <!-- Show disabled message for billable OUs -->
                        <vx-dropdown-item
                            v-else
                            data-test="delete-disabled"
                            disabled
                            title="Billable organizational units cannot be deleted"
                        >
                            <span class="text-gray-400">Delete</span>
                            <font-awesome-icon widthAuto :icon="faTrash" class="text-gray-400"></font-awesome-icon>
                        </vx-dropdown-item>
                    </template>
                </vx-dropdown>

                <vx-button
                    :href="`/settings/teams/${props.modelValue.team_id}/org-units/${props.modelValue.id}`"
                    data-test="edit"
                    outline=""
                    color="muted"
                    class="w-full lg:w-auto"
                >
                    Edit <font-awesome-icon widthAuto :icon="faFilePen" />
                </vx-button>
            </div>
        </vx-box>
    </div>
</template>

<script setup>
import { faEllipsis, faFilePen, faFolder, faFolderOpen, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBox,
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxIconLoading,
    VxSwal,
    VxTableRowItem,
} from '@voxie/frontend-components';
import { ref } from 'vue';
import { formatCount } from '~/components/filters';
import { orgUnitGroupsClient, orgUnitsClient, orgUnitUsersClient } from '~/services';
import { throwSwalRequestError } from '~/utils/request';

const isAdminOrOwner = Spark.isAdminOrOwner;

const props = defineProps({
    modelValue: {
        type: Object,
        required: true,
    },
});

const groupsCount = ref();
const loadingGroupsCount = ref(true);

const usersCount = ref('0');
const loadingUsersCount = ref(true);

const emit = defineEmits(['deleted']);

const getCounts = () => {
    orgUnitGroupsClient
        .countOrgUnitGroups(props.modelValue.team_id, props.modelValue.id)
        .then(({ data }) => {
            groupsCount.value = data.count;
        })
        .finally(() => {
            loadingGroupsCount.value = false;
        });

    orgUnitUsersClient
        .countOrgUnitUsers(props.modelValue.team_id, props.modelValue.id)
        .then(({ data }) => {
            usersCount.value = Intl.NumberFormat('en-US').format(data.count);
        })
        .finally(() => {
            loadingUsersCount.value = false;
        });
};

const deleteOrgUnit = () =>
    VxSwal.confirmation('Delete Organizational Unit', 'Are you sure? There is no undo.', 'danger', () =>
        orgUnitsClient
            .deleteOrgUnit(props.modelValue.team_id, props.modelValue.id)
            .then(() => emit('deleted', props.modelValue.id))
            .catch(throwSwalRequestError)
    );

getCounts();
</script>

<style scoped>
@reference "@css/style.css";

:deep(.vx-table-row-item) {
    @apply flex gap-2;
}

:deep(.vx-table-row-item > div:first-child) {
    @apply lg:hidden;
}
</style>
